import Loading from '@/components/Loading';
import { ControlGroupSelect } from '@/components/Studies/StudyGroups/Groups/ControlGroupSelect';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Radio from '@/components/UI/FormElements/Radio';
import Select from '@/components/UI/Select/Select';
import { successToast } from '@/helpers';
import { _isEmpty, _isNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { type GraphSetting } from '@/model/Preset.model';
import type { Study } from '@/model/Study.model';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import { ApiService } from '@/support/ApiService';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { useRequest } from '@/support/Hooks/request';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { isCancel } from 'axios';
import { type FC, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form-latest';
import { useParams } from 'react-router-dom';

const POST_TRACKING_DATE: GraphSetting = 'tracking_date';

const TRACKING_DATE_OPTION = { label: 'Tracking date', value: true } as const;
const DISEASE_INDUCTION_OPTION = { label: 'Disease induction date', value: false } as const;
const RELATIVE_DATE_OPTIONS = [TRACKING_DATE_OPTION, DISEASE_INDUCTION_OPTION];
const toRelativeDateOption = (value: boolean) => (value ? TRACKING_DATE_OPTION : DISEASE_INDUCTION_OPTION);

export interface ConfigurationProps {
  groups: Array<TreatmentGroup>;
  changeControlGroup: (id?: GroupApiId | string) => Promise<void>;
  canEdit: boolean;
}

const Configuration: FC<ConfigurationProps> = ({ canEdit = true, groups, changeControlGroup }) => {
  const { id: studyId } = useParams();
  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
  });

  if (studyLoading || _isNil(study)) {
    return (
      <div className="mv4 pa4">
        <Loading />
      </div>
    );
  }

  return <ConfigurationForm study={study} groups={groups} changeControlGroup={changeControlGroup} canEdit={canEdit} />;
};

interface ConfigurationFormProps {
  study: Study;
  groups: Array<TreatmentGroup>;
  changeControlGroup: (id?: GroupApiId | string) => Promise<void>;
  canEdit: boolean;
}

interface FormData {
  averageCalculation: Study['average_calculation'];
  errorDeviation: Study['error_deviation'];
  graphSettings: GraphSetting | undefined;
  excludeBeforeTrackingDate: boolean | undefined;
  useTrackingDateBaseline: boolean | undefined;
}

export const ConfigurationForm: FC<ConfigurationFormProps> = ({
  study,
  groups,
  changeControlGroup,
  canEdit = true,
}) => {
  const {
    id: studyId,
    api_id: studyApiId,
    average_calculation: averageCalculation,
    error_deviation: errorDeviation,
    settings: {
      graph_settings: graphSettings,
      exclude_before_tracking_date: excludeBeforeTrackingDate,
      use_tracking_date_baseline: useTrackingDateBaseline,
    },
  } = study;

  const [updatingGroupConfig, setUpdatingGroupConfig] = useState(false);
  const [defaultValues, setDefaultValues] = useState<FormData>({
    averageCalculation,
    errorDeviation,
    graphSettings,
    excludeBeforeTrackingDate,
    useTrackingDateBaseline,
  });
  const [excludeBeforeTrackingCheckboxDisabled, setExcludeBeforeTrackingCheckboxDisabled] = useState(
    graphSettings === POST_TRACKING_DATE
  );
  const [relativeDateDropdownDisabled, setRelativeDateDropdownDisabled] = useState(
    graphSettings === POST_TRACKING_DATE || excludeBeforeTrackingDate
  );

  const { sendRequest: updateStudy } = useRequest({
    route: 'studies.update',
    method: 'patch',
    params: { id: studyId },
  });
  const { control, handleSubmit, setValue, getValues } = useForm<FormData>({ defaultValues });
  const [watchedAverageCalculation, watchedErrorDeviation] = useWatch({
    control,
    name: ['averageCalculation', 'errorDeviation'],
  });

  const formDisabled = updatingGroupConfig || !canEdit;

  const onSubmit = async ({ averageCalculation, errorDeviation }: FormData) => {
    setUpdatingGroupConfig(true);
    await updateStudy({ average_calculation: averageCalculation, error_deviation: errorDeviation });
    setDefaultValues((prev) => ({ ...prev, averageCalculation, errorDeviation }));
    setUpdatingGroupConfig(false);
    successToast('Saved group settings.');
  };

  const handleGraphSettingsChange = async (value?: GraphSetting) => {
    if (_isNil(studyApiId)) {
      return;
    }
    try {
      const isPostTrackingDate = value === POST_TRACKING_DATE;
      const nextExcludeBeforeTrackingDate: boolean = isPostTrackingDate
        ? false
        : (getValues('excludeBeforeTrackingDate') ?? false);
      const nextUseTrackingDateBaseline: boolean =
        isPostTrackingDate || nextExcludeBeforeTrackingDate ? true : (getValues('useTrackingDateBaseline') ?? false);

      const response = await ApiService.call({
        endpoint: 'PATCH /api/v1/studies/{studyApiId}/settings',
        path: { studyApiId },
        body: {
          graph_settings: value,
          exclude_before_tracking_date: nextExcludeBeforeTrackingDate,
          use_tracking_date_baseline: nextUseTrackingDateBaseline,
        },
      });

      if (response.body?.success === true) {
        setDefaultValues((prev) => ({
          ...prev,
          graphSettings: value,
          excludeBeforeTrackingDate: nextExcludeBeforeTrackingDate,
          useTrackingDateBaseline: nextUseTrackingDateBaseline,
        }));
        setValue('graphSettings', value);
        setValue('excludeBeforeTrackingDate', nextExcludeBeforeTrackingDate);
        setValue('useTrackingDateBaseline', nextUseTrackingDateBaseline);
        setExcludeBeforeTrackingCheckboxDisabled(isPostTrackingDate);
        setRelativeDateDropdownDisabled(isPostTrackingDate || nextExcludeBeforeTrackingDate);
      }
    } catch (e) {
      if (!isCancel(e)) {
        ExceptionHandler.captureException(
          new InVivoError('Could not update study settings', { cause: e, slug: 'graph-settings-update' })
        );
      }
    }
  };

  const handleExcludeBeforeTrackingDateChange = (value?: boolean) => {
    if (value !== getValues('excludeBeforeTrackingDate')) {
      setValue('excludeBeforeTrackingDate', value);
      handleGraphSettingsChange(getValues('graphSettings'));
    }
  };

  const handleUseTrackingDateBaselineChange = (onChange: (value: boolean) => void, value: boolean) => {
    if (value !== getValues('useTrackingDateBaseline')) {
      onChange(value);
      handleGraphSettingsChange(getValues('graphSettings'));
    }
  };

  useEffect(() => {
    const autoSubmittableFieldChanged =
      watchedAverageCalculation !== defaultValues.averageCalculation ||
      watchedErrorDeviation !== defaultValues.errorDeviation;
    if (!updatingGroupConfig && autoSubmittableFieldChanged) {
      onSubmit(getValues());
    }
    // Auto-submit must trigger only when the watched fields change. `defaultValues`,
    // `updatingGroupConfig` and `onSubmit` are read fresh from the closure on each
    // run, so they intentionally stay out of the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAverageCalculation, watchedErrorDeviation]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="pv4 flex flex-wrap">
      <div className="ph4 w-28 mb4">
        <label className="mb3 db">How would you like to calculate averages?</label>
        <div>
          <Controller
            control={control}
            name="averageCalculation"
            defaultValue={averageCalculation}
            render={({ field: { onChange, value } }) => (
              <>
                <Radio
                  id="arithmetic"
                  name="average_calculation"
                  checked={value === 'arithmetic'}
                  disabled={formDisabled}
                  value="arithmetic"
                  label="Arithmetic"
                  onChange={onChange}
                  className="mb2"
                />
                <Radio
                  id="geometric"
                  name="average_calculation"
                  checked={value === 'geometric'}
                  disabled={formDisabled}
                  value="geometric"
                  label="Geometric"
                  onChange={onChange}
                />
              </>
            )}
          />
        </div>
      </div>
      <div className="ph4 w-28 mb4">
        <label className="mb3 db">How would you like to display variation?</label>
        <div>
          <Controller
            control={control}
            name="errorDeviation"
            defaultValue={errorDeviation}
            render={({ field: { onChange, value } }) => (
              <>
                <Radio
                  id="sd"
                  name="error_deviation"
                  checked={value === 'sd'}
                  disabled={formDisabled}
                  value="sd"
                  label="Standard deviation"
                  onChange={onChange}
                  className="mb2"
                />
                <Radio
                  id="sem"
                  name="error_deviation"
                  checked={value === 'sem'}
                  disabled={formDisabled}
                  value="sem"
                  label="Standard error of the mean"
                  onChange={onChange}
                />
              </>
            )}
          />
        </div>
      </div>
      <div className="ph4 w-28 mb4">
        <label className="mb3 db">Days Plotted</label>
        <div data-testid="group-config-days-plotted">
          <Controller
            control={control}
            name="graphSettings"
            defaultValue={graphSettings}
            render={({ field: { value } }) => {
              const onGraphSettingChange = (e: React.ChangeEvent<HTMLInputElement>) =>
                handleGraphSettingsChange(e.target.value as GraphSetting);
              return (
                <>
                  <Radio
                    id="td"
                    name="graph_settings"
                    checked={value === 'tracking_date'}
                    disabled={formDisabled}
                    value="tracking_date"
                    label="Post tracking date"
                    onChange={onGraphSettingChange}
                    className="mb2"
                  />
                  <Radio
                    id="di"
                    name="graph_settings"
                    checked={value === 'disease_induction'}
                    disabled={formDisabled}
                    value="disease_induction"
                    label="Post disease induction date"
                    onChange={onGraphSettingChange}
                  />
                </>
              );
            }}
          />
          <Controller
            control={control}
            name="excludeBeforeTrackingDate"
            defaultValue={false}
            render={({ field: { value } }) => (
              <Checkbox
                id="ebtd"
                name="exclude_before_tracking_date"
                checked={value}
                disabled={formDisabled || excludeBeforeTrackingCheckboxDisabled}
                label="Exclude data before tracking date"
                onChange={(e) => handleExcludeBeforeTrackingDateChange(e.target.checked)}
                className="mt2 ml4"
              />
            )}
          />
        </div>
        <label className="mb3 mt3 db">Date for relative calculations</label>
        <div data-testid="group-config-relative-date">
          <Controller
            control={control}
            name="useTrackingDateBaseline"
            defaultValue={useTrackingDateBaseline}
            render={({ field: { value, onChange } }) => (
              <Select
                value={toRelativeDateOption(value === true)}
                options={RELATIVE_DATE_OPTIONS}
                onChange={(option) => handleUseTrackingDateBaselineChange(onChange, option?.value ?? true)}
                disabled={formDisabled || relativeDateDropdownDisabled}
                isMulti={false}
              />
            )}
          />
        </div>
      </div>
      <div className="ph4 w-28">
        <label className="mb3 db">Control group</label>
        <ControlGroupSelect
          disabled={!canEdit || _isEmpty(groups)}
          groups={groups ?? []}
          onChange={changeControlGroup}
        />
      </div>
    </form>
  );
};

export default Configuration;
